import React, { useEffect, useMemo, useState } from "react";
import { Plus } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { useLocaleFormat } from "../i18n/useLocaleFormat";
import { useTour } from "../lib/tours/useTour";
import { useDocumentsSteps } from "../lib/tours/steps/documents";
import TourReplayButton from "../lib/tours/TourReplayButton";

import GuardianPortfolioPanel, { GuardianCurrencyValue } from "../components/qenva/GuardianPortfolioPanel";
import CategoryGrid from "../components/documents/CategoryGrid";
import DocumentsFilterBar from "../components/documents/DocumentsFilterBar";
import DocumentsTable from "../components/documents/DocumentsTable";
import BulkActionsBar from "../components/documents/BulkActionsBar";
import DocumentsDropZone from "../components/documents/DocumentsDropZone";
import { DOC_CATEGORIES } from "../components/documents/DocTypeIcon";
import { deriveDocStatus } from "../components/documents/DocStatusPill";
import SuggestionCard from "../components/SuggestionCard";
import { SUGGESTION_DEFINITIONS } from "../lib/suggestions/suggestionsManifest";
import { useSuggestions } from "../lib/suggestions/useSuggestions";

/**
 * QENVA Documents module — production page.
 *
 * Composition (top → bottom):
 *   1. Header  : breadcrumb + title + subtitle
 *   2. Hero    : <GuardianPortfolioPanel variant="documents"/>  (DS shared)
 *   3. Grid    : <CategoryGrid/>           (8 categories selector)
 *   4. Card    : Filter bar + table        (full-width white card)
 *   5. DropZone: inline upload affordance
 *
 * Data sources :
 *   GET /documents               → full list (workspace-scoped)
 *   GET /alm/alerts?status=active → active alert count per document_id
 *   GET /dashboard/executive-status + /documents/stats → metrics for the panel
 */
export default function Documents() {
  const { t } = useTranslation("documents");
  const { t: tSuggestions } = useTranslation("suggestions");
  const { formatNumber } = useLocaleFormat();
  const navigate = useNavigate();

  const [docs, setDocs] = useState([]);
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [loading, setLoading] = useState(true);
  const [riskCounts, setRiskCounts] = useState({});
  const [nextDueByDoc, setNextDueByDoc] = useState({});
  const [lastAnalyzedIso, setLastAnalyzedIso] = useState(null);
  const [exec, setExec] = useState(null);
  const [docStats, setDocStats] = useState(null);

  // UI filters
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [category, setCategory] = useState("all");

  const documentsSteps = useDocumentsSteps();
  const { Tour, replay } = useTour("documents", documentsSteps);

  // Load documents + active alerts + executive aggregates in parallel
  const refresh = () => {
    setLoading(true);
    Promise.all([
      api.get("/documents", { params: q ? { q: q.trim() } : {} }).catch(() => ({ data: [] })),
      api.get("/alm/alerts", { params: { status: "active", limit: 500 } }).catch(() => ({ data: { items: [] } })),
      api.get("/dashboard/executive-status").catch(() => ({ data: null })),
      api.get("/documents/stats").catch(() => ({ data: null })),
    ]).then(([docsRes, alertsRes, execRes, statsRes]) => {
      const items = Array.isArray(docsRes.data) ? docsRes.data : (docsRes.data?.items || []);
      setDocs(items);
      setExec(execRes.data);
      setDocStats(statsRes.data);
      // Most-recent analysis timestamp drives the "Analysé il y a 2h" pill.
      const latest = items
        .map((d) => d.updated_at || d.created_at)
        .filter(Boolean)
        .sort()
        .reverse()[0];
      setLastAnalyzedIso(latest || null);
      // Aggregate alerts → counts + earliest due per document
      const counts = {};
      const earliest = {};
      const alerts = alertsRes.data?.items || [];
      for (const a of alerts) {
        const did = a.document_id;
        if (!did) continue;
        counts[did] = (counts[did] || 0) + 1;
        if (a.due_at) {
          if (!earliest[did] || a.due_at < earliest[did]) earliest[did] = a.due_at;
        }
      }
      setRiskCounts(counts);
      // Also pull invoice due_date from enrichment so that invoices without
      // alerts still show their next deadline (and feed the sortable column).
      for (const d of items) {
        const docType = (d.document_type || "").toLowerCase();
        if (docType !== "invoice") continue;
        const invDue = d.enrichment?.due_date;
        if (!invDue) continue;
        if (!earliest[d.id] || invDue < earliest[d.id]) {
          earliest[d.id] = invDue;
        }
      }
      setNextDueByDoc(earliest);
    }).finally(() => setLoading(false));
  };

  useEffect(refresh, [q]);

  // Bulk-selection is scoped to the current filtered view — clear it
  // whenever the active filters change so the toolbar can never reference
  // a document that's no longer visible.
  useEffect(() => {
    setSelectedIds(new Set());
  }, [q, statusFilter, category]);

  const handleToggleOne = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const handleToggleAll = (ids) => {
    setSelectedIds((prev) => {
      const allSelected = ids.length > 0 && ids.every((id) => prev.has(id));
      if (allSelected) {
        const next = new Set(prev);
        ids.forEach((id) => next.delete(id));
        return next;
      }
      return new Set([...prev, ...ids]);
    });
  };

  const handleClearSelection = () => setSelectedIds(new Set());

  const handleBulkActionDone = () => {
    setSelectedIds(new Set());
    refresh();
  };

  // Category counts — derived from current docs (always reflects the workspace)
  const categoryCounts = useMemo(() => {
    const out = { all: docs.length };
    for (const cat of DOC_CATEGORIES) {
      if (cat.key === "all") continue;
      out[cat.key] = 0;
    }
    for (const d of docs) {
      const k = (d.document_type || "").toLowerCase();
      if (out[k] != null) out[k]++;
    }
    return out;
  }, [docs]);

  // Filtered list — category + status filter applied client-side (workspace
  // sizes are small enough; for very large libs we'll move this to backend).
  const filteredDocs = useMemo(() => {
    return docs.filter((d) => {
      if (category !== "all" && (d.document_type || "").toLowerCase() !== category) return false;
      const st = deriveDocStatus(d);
      if (statusFilter === "alert"     && st !== "alert")    return false;
      if (statusFilter === "monitor"   && st !== "monitor")  return false;
      if (statusFilter === "analyzed"  && (d.status !== "completed")) return false;
      if (statusFilter === "inactive"  && (d.status === "completed")) return false;
      return true;
    });
  }, [docs, category, statusFilter]);

  const watchedCount = useMemo(
    () => docs.filter((d) => (riskCounts[d.id] || 0) > 0 || deriveDocStatus(d) !== "inactive").length,
    [docs, riskCounts]
  );

  // Build the metrics array fed to the shared Guardian panel
  const portfolioMetrics = useMemo(() => {
    const surveillanceCount = (docStats?.high_risk_count || 0) + (docStats?.medium_risk_count || 0) + (docStats?.low_risk_count || 0);
    const activeAlerts = exec?.exposure?.active_count ?? docStats?.active_alerts ?? 0;
    const opportunities = exec?.opportunities?.count ?? 0;
    const exposure = exec?.exposure?.totals_by_currency || {};
    const preserved = exec?.value_preserved?.totals_by_currency || {};
    return [
      { key: "total",         value: formatNumber(docs.length),       label: t("portfolio.kpi_total"),         sub: t("portfolio.kpi_total_sub"),         tone: "info",    testId: "kpi-total-docs" },
      { key: "watch",         value: formatNumber(surveillanceCount), label: t("portfolio.kpi_watch"),         sub: t("portfolio.kpi_watch_sub"),         tone: "success", testId: "kpi-watch" },
      { key: "alerts",        value: formatNumber(activeAlerts),      label: t("portfolio.kpi_alerts"),        sub: t("portfolio.kpi_alerts_sub"),        tone: "danger",  testId: "kpi-alerts" },
      { key: "opportunities", value: formatNumber(opportunities),     label: t("portfolio.kpi_opportunities"), sub: t("portfolio.kpi_opportunities_sub"), tone: "warning", testId: "kpi-opportunities" },
      { key: "exposure",      value: <GuardianCurrencyValue totals={exposure} />, label: t("portfolio.kpi_exposure"),  sub: t("portfolio.kpi_exposure_sub"),  tone: "white",   testId: "kpi-exposure" },
      { key: "preserved",     value: <GuardianCurrencyValue totals={preserved} />, label: t("portfolio.kpi_preserved"), sub: t("portfolio.kpi_preserved_sub"), tone: "success", testId: "kpi-preserved" },
    ];
  }, [docs.length, docStats, exec, formatNumber, t]);

  const documentsSuggestionDefs = SUGGESTION_DEFINITIONS.filter((d) => d.page === "documents");
  const suggestionPageData = { docs, docStats };
  const { visible: visibleSuggestions, dismiss: dismissSuggestion } = useSuggestions(documentsSuggestionDefs, suggestionPageData);

  return (
    <div className="animate-fade-up" data-testid="documents-page-v2">
      {Tour}
      {/* H1 + subtitle (light surface, sits above the navy panel) */}
      <div className="mb-7 max-w-3xl flex items-end justify-between gap-4 flex-wrap">
        <div className="min-w-0">
          <p className="text-[12.5px] text-zinc-500" data-testid="documents-breadcrumb">
            <span className="text-qenva-navy font-medium">{t("page_title")}</span>
            <span className="mx-2 text-zinc-300">—</span>
            {t("page_breadcrumb")}
          </p>
          <h1 className="font-display font-extrabold text-[34px] sm:text-[42px] text-qenva-navy tracking-tight leading-[1.1] mt-2">
            {t("page_title")}
          </h1>
          <p className="text-[14px] text-zinc-500 mt-2">
            {t("page_subtitle", { total: docs.length, watched: watchedCount })}
          </p>
        </div>
        <TourReplayButton onClick={replay} />
      </div>

      {/* 1. Guardian portfolio hero — shared DS component */}
      <GuardianPortfolioPanel
        kicker={t("portfolio.kicker")}
        metrics={portfolioMetrics}
        lastAnalysis={lastAnalyzedIso ? { iso: lastAnalyzedIso } : undefined}
        primaryAction={{ label: t("add_cta"), icon: Plus, onClick: () => navigate("/upload") }}
        variant="documents"
        loading={loading}
        testId="portfolio-guardian-block"
        className="mb-8"
      />

      {/* 2. Category grid */}
      {visibleSuggestions.map((def) => (
        <SuggestionCard
          key={def.key}
          suggestionKey={def.key}
          icon={def.icon}
          title={tSuggestions(`${def.key}.title`, def.countFn ? { count: def.countFn(suggestionPageData) } : undefined)}
          subtitle={tSuggestions(`${def.key}.subtitle`)}
          ctaLabel={tSuggestions(`${def.key}.cta`)}
          ctaHref={def.ctaHrefFn ? def.ctaHrefFn(suggestionPageData) : def.ctaHref}
          onDismiss={() => dismissSuggestion(def.key)}
        />
      ))}

      <CategoryGrid counts={categoryCounts} selected={category} onSelect={setCategory} />

      {/* 3. Filter bar + table card */}
      <section
        data-testid="documents-card"
        className="bg-white border border-zinc-200 rounded-2xl overflow-hidden"
      >
        <DocumentsFilterBar
          q={q}
          onQ={setQ}
          status={statusFilter}
          onStatus={setStatusFilter}
        />
        <BulkActionsBar
          selectedIds={selectedIds}
          docs={filteredDocs}
          onClear={handleClearSelection}
          onDone={handleBulkActionDone}
        />
        <DocumentsTable
          docs={filteredDocs}
          riskCounts={riskCounts}
          nextDueByDoc={nextDueByDoc}
          loading={loading}
          onRefresh={refresh}
          selectedIds={selectedIds}
          onToggleOne={handleToggleOne}
          onToggleAll={handleToggleAll}
        />
        <footer className="px-5 py-3 border-t border-zinc-100 text-[12px] text-zinc-500 flex items-center justify-between gap-3 flex-wrap">
          <span data-testid="documents-shown-count">
            {t("table.shown_count", { count: filteredDocs.length })}
          </span>
          {filteredDocs.length !== docs.length && (
            <button
              type="button"
              onClick={() => { setQ(""); setStatusFilter("all"); setCategory("all"); }}
              className="text-qenva-blue hover:underline"
              data-testid="clear-filters-link"
            >
              {t("table.clear_filters")}
            </button>
          )}
        </footer>
      </section>

      {/* 4. Inline drop zone */}
      <DocumentsDropZone onUploaded={refresh} />
    </div>
  );
}
